import {OnInit, OnDestroy} from '@angular/core';
import {FormGroup, AbstractControl, FormArray, Validators, FormControl, ValidatorFn} from '@angular/forms';
import {AlertService} from '../_services';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {Subject} from "rxjs";
import {FormatRule, getFormatRuleMessage} from '../_models';

export interface FormError {
  error: string;
  params: any;
}

export enum FormControlStatus {
  Valid = "VALID",
  Invalid = "INVALID"
}

export abstract class Form implements OnInit, OnDestroy {
  loading = false;
  title: string = '';
  description: string = ''
  hideWidgetBackground = false;
  form: FormGroup;
  submitted = false;

  protected _destroy$: Subject<boolean> = new Subject<boolean>();
  protected initialValue = {};

  constructor(
    protected alertService: AlertService,
    protected router: Router,
    protected location: Location
  ) { }

  ngOnInit() {
  }

  ngOnDestroy() {
    this._destroy$.next(true);
    this._destroy$.unsubscribe();
  }

  protected setTitle(title) {
    this.title = title;
  }

  protected setForm(form: FormGroup) {
    this.form = form;
    this.initialValue = form.value;
  }

  protected resetForm(scrollToTop: boolean = false) {
    this.submitted = false;
    this.loading = false;
    this.form.reset(this.initialValue);

    if (scrollToTop) {
      window.scroll(0, 0);
    }
  }

  goBack() {
    this.location.back();
  }

  reload() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  isFieldVisible(fieldName: string) {
    if (fieldName in this.form.controls) {
      return true;
    }

    return false;
  }

  isFieldRequired(fieldname: string): boolean {
    const control = this.getControl(fieldname);

    if (control && control.validator) {
      const validator = control.validator({} as AbstractControl);

      return validator && validator.required;
    }

    return false;
  }

  setRequired(fieldName: string, required: boolean, extra: ValidatorFn[] = []) {
    const control = this.form.get(fieldName);

    if (control) {
      const validators = [...extra];

      if (required) {
        validators.push(Validators.required);
      }

      control.setValidators(validators);
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  toggleBooleanField(event, fieldname: string) {
    const control = this.getControl(fieldname);
    control.markAsDirty();
    control.patchValue(!!event.target.checked);
  }

  isBooleanFieldChecked(fieldname: string): boolean {
    const control = this.getControl(fieldname);
    return control ? control.value : false;
  }

  isFieldEmpty(fieldname: string): boolean {
    const control = this.getControl(fieldname);
    return control ? !control.value : true;
  }

  isFieldEqual(fieldname: string, value: any) {
    const control = this.getControl(fieldname);
    return control && (control.value.toString() === value.toString());
  }

  get destroy$() {
    return this._destroy$.asObservable();
  }

  public formErrors(): FormError[] {
    if (this.submitted && this.form.errors) {
      return this.getErrors(this.form);
    }
  }

  public fieldErrors(name: string): FormError[] {
    let control = this.findFieldControl(name);
    if (control && (control.touched || this.submitted) && control.errors) {
      return this.getErrors(control);
    }

    return undefined;
  }

  public isFieldValid(name: string): boolean {
    const control = this.getControl(name);
    return control && control.valid && (control.touched || control.dirty);
  }

  public isFieldInvalid(name: string): boolean {
    const control = this.getControl(name);
    return control && control.invalid && (control.touched || control.dirty);
  }

  public getControl(name: string) {
    let control: any = this.form;
    name.split(".").forEach(field => {
      control = control.get(field);
    });
    return control
  }

  public getControlValue(name: string) {
    const control = this.getControl(name);

    return control ? control.value : null;
  }

  public getControlValueText(path: string): string {
    const control = this.form.get(path);
    const value = control ? control.value : null;
    let text = '-';

    if (value !== null && value !== undefined && String(value).length > 0) {
      text = String(value);
    }

    return text;
  }

  public mustMatch(controlName: string, matchingControlName: string) {
    return (formGroup: FormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        return;
      }

      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    }
  }

  private getLastSegmentWithDotAndUppercase(inputString: string, label: string): string {
    if (label.length > 0) {
      return label
    } else {
      let lastPart = inputString;
      if (inputString.includes('.')) {
        const parts = inputString.split('.');
        lastPart = parts.length > 1 ? parts[parts.length - 1] : '';
      }
      if (lastPart.length > 0) {
        lastPart = lastPart.charAt(0).toUpperCase() + lastPart.slice(1);
      }
      return lastPart;
    }
  }

  public getAppropriateError(name: string, showOnSubmit: boolean = false, label: string = ''): string {
    let errors = this.fieldErrors(name);
    let formattedString = this.getLastSegmentWithDotAndUppercase(name, label);
    if (formattedString.includes('_')) {
      formattedString = formattedString.replace(/_/g, ' '); // Replace underscores with spaces
    }
    let error = errors && errors.length ? errors[0] : false;
    let forceDisplay = false;
    if (!error) {
      return "";
    }
    let errorMsg = error.error;
    switch (errorMsg) {
      case "minlength":
        let length = error.params.requiredLength;
        errorMsg = `must be at least ${length} characters`;
        break;
      case "maxlength":
        let maxLength = error.params.requiredLength;
        errorMsg = `must be at most ${maxLength} characters`
        break;
      case "email":
        errorMsg = "Invalid Email"
        break;
      case "pattern":
        errorMsg = "Invalid Pattern"
        break;
      case "mustMatch":
        errorMsg = "Field doesn't match"
        break;
      case "required":
        errorMsg = `${formattedString} is required`
        break;
      case "max":
        errorMsg = "Input is exceeding max limit"
        break;
      case "string":
        errorMsg = error.params.string
        forceDisplay = true
        break;
      default:
        break;
    }
    if (forceDisplay) {
      return errorMsg.charAt(0).toUpperCase() + errorMsg.substr(1).toLowerCase();
    }
    let control = this.getControl(name);
    if (showOnSubmit && !this.submitted) {
      return null
    } else if (showOnSubmit) {
      return errorMsg.charAt(0).toUpperCase() + errorMsg.substr(1).toLowerCase();
    }
    if (!control.dirty) {
      return null;
    }
    return errorMsg.charAt(0).toUpperCase() + errorMsg.substr(1).toLowerCase();
  }

  public getAppropriateFormError(name: string) {
    let error = this.getAppropriateError(name, true);
    if (!error) {
      return []
    }
    return [{error: 'string', params: {'string': error}}]
  }

  public resetFieldErrors(name: string): void {
    this.form.get(name).setErrors(null);
  }

  protected handleError(error: any, keepAfterNavigationChange: boolean = false): void {
    let msg = 'Something went wrong, Please try again.';
    const status = error && error.status;

    switch (status) {
      case 401: {
        this.router.navigate(['/user/login']);
        this.alertService.error('Session expired. Please login again.', true);
        break;
      }
      case 400: {
        console.error('Error 400:', error);
        this.alertService.error(msg, keepAfterNavigationChange);
        break;
      }
      case 500: {
        msg = 'An internal server error occurred.';
        console.error('Error 500:', error);
        this.alertService.error('Error ' + status + ': ' + msg, keepAfterNavigationChange);
        break;
      }
      case 409: {
        msg = 'Duplicate record found.';
        this.alertService.error('Error ' + status + ': ' + msg, keepAfterNavigationChange);
        break;
      }
      case 404: {
        msg = 'Resource not found.';
        this.alertService.error('Error ' + status + ': ' + msg, keepAfterNavigationChange);
        break;
      }
      case 403: {
        msg = 'You do not have permission to perform this action.';
        this.alertService.error('Error ' + status + ': ' + msg, keepAfterNavigationChange);
        break;
      }
      case 0: {
        msg = 'Network error, please check your connection.';
        this.alertService.error('Error: ' + msg, keepAfterNavigationChange);
        break;
      }
      default: {
        if (error && typeof error === 'object') {
          const backendMsg = this.extractApiErrorMessage(error);
          if (backendMsg) {
            msg = backendMsg;
          } else if (error.statusText) {
            msg = error.statusText;
          }
          this.alertService.error(msg, keepAfterNavigationChange);
        } else {
          this.alertService.error('Error: ' + msg, keepAfterNavigationChange);
        }
        break;
      }
    }
  }

  protected handleSubmitError(error: any, keepAfterNavigationChange: boolean = false, showAlert: boolean = false) {
    if (error.status === 400) { // bad request
      if (Array.isArray(error)) {
        this.alertService.error(error[0], keepAfterNavigationChange);
        return;
      }
      const data = this.flattenObj(error.error);
      const fields = Object.keys(data || {});
      fields.forEach((field) => {
        const errors = this.fetchFieldErrors(data, field);
        let control: any = this.form;
        // A key with no matching control (e.g. a non-field error) ends the walk and falls back to an alert
        field.split(".").forEach(field => {
          if (control) {
            control = control.get(field);
          }
        });

        if (control) {
          control.setErrors(errors);
        }
        if (!control || showAlert) {
          this.alertService.error(errors.string, keepAfterNavigationChange);
        }
      });
    } else {
      this.handleError(error, keepAfterNavigationChange);
    }
  }

  formArrayFieldError(name: string, index?: number) {
    const control = this.getFormArray(name);
    if (control && control.controls && control.controls.length) {
      if (index === undefined || index === null) {
        return this.getErrors(control.controls.find((control) => control.invalid));
      } else if (control.controls[index] && control.controls[index].invalid){
        return this.getErrors(control.controls[index]);
      }
    }
  }

  getFormatError(fieldName: string, label: string, rule: FormatRule, prefix: string = ''): string {
    const control = this.form.get(fieldName);
    let message = this.getAppropriateError(fieldName, true, label);

    if (message && rule && control && control.errors && !control.errors.required && !control.errors.string) {
      message = getFormatRuleMessage(prefix + label, rule);
      message = message.charAt(0).toUpperCase() + message.slice(1);
    }

    return message;
  }

  protected getErrors(control: AbstractControl): FormError[] {
    let errors = control.errors

    if (!errors) {
      return []
    }

    return Object.keys(errors)
      .filter((error) => errors[error])
      .map((error) => {
        let params = errors[error];
        return {
          error: error,
          params: (error === 'string') ? {'string': params} : (params === true || params === {} ? null : params)
        };
      });
  }

  public hasFieldError(formArrayName: string, fieldName: string): boolean {
    const formArray = this.form.get(formArrayName) as FormArray;
    return formArray.controls.some(
      (group) => group.get(fieldName).errors && (group.get(fieldName).touched || this.submitted)
    );
  }

  public hasControlError(fieldName: string): boolean {
    const control = this.form.get(fieldName);
    return !!(control && control.errors && (control.touched || this.submitted));
  }

  public hasFormGroupError(fieldset: string, fieldName: string): boolean {
    const group = this.form.get(fieldset) as FormGroup;
    const control = group.get(fieldName) as FormControl;

    return !!(control && control.errors && (control.touched || this.submitted));
  }

  public isFormGroupValid(groupName: string): boolean {
    const targetGroup = this.form.get(groupName) as FormGroup;
    let isValid = true;

    if (targetGroup) {
      Object.keys(targetGroup.controls).forEach((subgroupKey) => {
        const subgroup = targetGroup.get(subgroupKey) as FormGroup;
        if (subgroup) {
          Object.keys(subgroup.controls).forEach((controlKey) => {
            const control = subgroup.get(controlKey);
            if (control) {
              const validator = control.validator ? control.validator({} as any) : null;
              const isRequired = validator && validator['required'];
              const isEmpty = control.value === null || control.value === '';

              if (isRequired && isEmpty) {
                isValid = false;
              }
            }
          });
        }
      });
    }

    return isValid;
  }

  public hasFieldErrorAtIndex(formArrayName: string, index: number, fieldName: string): boolean {
    const formArray = this.form.get(formArrayName) as FormArray;
    const control = formArray.at(index).get(fieldName);

    return !!(control && control.errors && (control.touched || this.submitted || formArray.touched));
  }

  protected findFieldControl(field: string): AbstractControl {
    let control: AbstractControl;
    if ((field === 'base') || !this.form) {
      control = this.form;
    } else if (this.form.contains(field)) {
      control = this.form.get(field);
    } else if (field.match(/_id$/) && this.form.contains(field.substring(0, field.length - 3))) {
      control = this.form.get(field.substring(0, field.length - 3));
    } else if (field.indexOf('.') > 0) {
      let group = this.form;
      field.split('.').forEach((f) => {
        if (group.contains(f)) {
          control = group.get(f);
          if (control instanceof FormGroup) {
            group = control;
          }
        } else {
          control = group;
        }
      })
    } else {
      // Field is not defined in form but there is a validation error for it, set it globally
      control = this.form;
    }
    return control;
  }

  getFormGroup(groupName) {
    return this.form.controls[groupName] as FormGroup;
  }

  getFormArray(arrayName) {
    return this.form.get(arrayName) as FormArray;
  }

  public clearFormArray(arrayName: FormArray) {
    while (arrayName.length !== 0) {
      arrayName.removeAt(0)
    }
  }

  private fetchFieldErrors(data: any, field: string): any {
    const errors = {};
    if (!(field in data) || !data[field]) {
      return errors;
    }

    let field_errors = data[field];
    if (!Array.isArray(field_errors)) {
      field_errors = [field_errors]
    }
    field_errors.forEach((e) => {
      if (typeof(e) === 'string') {
        errors['string'] = e;
      } else {
        let name: string = e.error;
        if (!name) {
          Object.keys(e).forEach((key) => {
            errors[key] = e;
          });
        } else {
          delete e.error;
          errors[name] = e;
        }
      }
    });
    return errors;
  }

  // Pull a readable message out of the error response body, whatever shape it takes.
  private extractApiErrorMessage(error: any): string {
    let message: string = null;
    if (error && error.error) {
      message = this.firstErrorString(error.error);
    }
    return message;
  }

  private firstErrorString(value: any): string {
    let message: string = null;
    if (typeof value === 'string') {
      message = value.trim() || null;
    } else if (Array.isArray(value)) {
      for (const item of value) {
        if (!message) {
          message = this.firstErrorString(item);
        }
      }
    } else if (value && typeof value === 'object') {
      // Prefer the top-level message, otherwise take the first field error.
      const keys = value.hasOwnProperty('detail')
        ? ['detail', ...Object.keys(value).filter((key) => key !== 'detail')]
        : Object.keys(value);
      for (const key of keys) {
        if (!message) {
          message = this.firstErrorString(value[key]);
        }
      }
    }
    return message;
  }

  private isPlainObj(o: any): boolean {
    return o && o.constructor && o.constructor.prototype && o.constructor.prototype.hasOwnProperty("isPrototypeOf")
  }

  private flattenObj(obj: Object, keys = []): Object {
    return Object.keys(obj).reduce((acc, key) => {
      return Object.assign(acc, this.isPlainObj(obj[key])
        ? this.flattenObj(obj[key], keys.concat(key))
        : {[keys.concat(key).join(".")]: obj[key]}
      )
    }, {})
  }

}
